// Automatic zoom ("Display size") — docs/specs/responsive-windowed.md,
// phase 3 (James 2026-10-27, decisions 5-8): pages rearrange first (menu →
// rail → ☰ drawer, lib/shellLayout.js), THEN the whole app scales between 70 %
// and 200 % by window size and screen density, so a 720p or short window fits
// and a 4K screen isn't tiny.  A "Display size" (Profile: Auto / Smaller /
// Larger), kept per person on each computer, adjusts it.
//
// James's 2026-10-29 answers (spec "Post-code review (2026-10-29)"):
//   D1 a TALL window grows by its height even when narrower than 1920, while
//      the page keeps about 1536 px of room (AUTO_MIN_ROOM);
//   D2 on a screen the OS scales 150 % or more, Auto's down-zoom stops at
//      0.85 (DENSE_AUTO_FLOOR); Smaller can still reach 0.70;
//   D3 Display size per signed-in person on each computer
//      ('lextrack_display_size:<user id>'); nobody signed in, the sign-in
//      page, the client portal and every other signed-out page use Auto
//      (claimDisplaySizeFor, AuthContext; AUTO_ONLY_PATHS);
//   D4 the breakpoint cap leaves out 2xl (1536), so Larger is 1.15 on a
//      maximised 1080p laptop at 125 % (CAP_BREAKPOINTS).
//
// HOW (option B, root-font zoom): the zoom is the CSS variable `--app-zoom`
// on <html>; index.css multiplies the theme's Font Size root
// (13/14/16/18 px) by it, so everything sized in rem (Tailwind spacing, text,
// widths, icons) follows, and the px text classes text-[9-12px] follow
// through their own overrides there.  Nothing is transformed: layout px ARE
// event px, so getBoundingClientRect / clientX agree in the desktop app's
// Chromium 120 and in current Chrome alike (CSS `zoom` does not — measured,
// see the spec).  Media queries and the shell tiers keep reading the real
// window width.  `data-app-zoom` mirrors the value for tests and the layout
// audit.
//
// Never scaled: /popup/* windows and the timer bar (fixed-size windows
// designed at 100 %: zoom 1), paper (`@media print` forces --app-zoom to 1;
// JS readers get 1 between beforeprint and afterprint), and lib/pdf.js
// exports (jsPDF primitives, no DOM).
//
// Pure rules first (lib/displayZoom.test.js pins every audited window), then
// the runtime that watches the window (startDisplayZoom, called by index.js
// before the first render).

// The person's choice is stored under DISPLAY_SIZE_KEY + ':' + user id
// (displaySizeKeyFor).  The bare key is the per-computer value from before
// D3: claimDisplaySizeFor hands it to the first person who signs in.
export const DISPLAY_SIZE_KEY = 'lextrack_display_size'
export const DISPLAY_SIZES = Object.freeze(['auto', 'smaller', 'larger'])
export const SIZE_MULTIPLIER = Object.freeze({ auto: 1, smaller: 0.85, larger: 1.15 })

export const ZOOM_MIN = 0.7
export const ZOOM_MAX = 2
export const ZOOM_STEP = 0.05
export const HYSTERESIS = 0.035
export const SETTLE_MS = 200

// Auto.  Below the rail tier (phones, the ☰ drawer) the app is never scaled.
export const MIN_ZOOM_WIDTH = 768
// Up (D1): a window taller than a maximised 1080p desktop window (≈980)
// grows by its HEIGHT, along a curve that reaches 2.0 at twice the height,
// but never so far that the page has less than AUTO_MIN_ROOM px of room
// (W / zoom): 1536, the widest layout (Tailwind 2xl).  So a 4K screen at
// 100 % gives 2.0 maximised (3840×2070), 1.25 snapped to half (1920 wide)
// and 1.00 snapped to a third (1280 wide: under 1536, never grown).
export const UP_REF_HEIGHT = 1000
export const UP_LIMIT = 2
export const UP_CURVE = 1.7
export const AUTO_MIN_ROOM = 1536
// Down: by HEIGHT only — widths are handled by rearranging (phase 2 fits
// 640 px wide at 100 %); a window shorter than 760 shrinks so less scrolls.
export const DOWN_REF_HEIGHT = 760
// D2: on a screen the OS scales 150 % or more (Windows display scaling, a
// Retina Mac), Auto's down-zoom stops at 0.85 — the person chose big text
// there.  Below 150 % the floor is ZOOM_MIN.  The OS scale is
// devicePixelRatio ÷ the person's own page zoom.
export const DENSE_OS_SCALE = 1.5
export const DENSE_AUTO_FLOOR = 0.85

// "Rearrange first": a zoom above 1 must leave the page at least the room
// the layout the real width chose was built for — the Tailwind breakpoints
// (sm 640, md 768 = the rail, lg 1024, xl 1280 = the full menu) and, below
// 640, a 375 px phone.  Down-zoom only adds room.  2xl (1536) is left out
// of the cap (D4): its only layouts, Reports' menu at a fifth and the report
// beside it at four fifths, were measured to fit at 1536 × 1.15 (1336 px of
// room), so from 1536 the cap is w / 1280.  BREAKPOINTS stays Tailwind's.
export const BREAKPOINTS = Object.freeze([640, 768, 1024, 1280, 1536])
export const CAP_BREAKPOINTS = Object.freeze([640, 768, 1024, 1280])
export const NARROW_DESIGN_WIDTH = 375

// Smallest on-screen text: 9 px, the smallest size the app showed before.
// index.css floors text-[9-12px], text-xs and text-sm with it; charts use
// zoomFontPx.  Layout (rem spacing) still shrinks to 0.70.
export const MIN_TEXT_PX = 9

// The person's own page zoom.  Chrome / Edge presets (Ctrl +/-) and the
// desktop app's View → Zoom (main.js: zoomLevel −2…+3 in 0.5 steps, factor
// 1.2^level).
export const BROWSER_ZOOM_STEPS = Object.freeze([0.25, 1 / 3, 0.5, 2 / 3, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3])
export const DESKTOP_ZOOM_STEPS = Object.freeze([-4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6].map(k => Math.pow(1.2, k / 2)))
// Windows / macOS display scales a devicePixelRatio can carry.
export const OS_SCALES = Object.freeze([1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 3])
const SNAP_TOLERANCE = 0.02
const OS_TOLERANCE = 0.01
// A window frame (Windows: 8 px a side) is no zoom, even where it is over
// 2 % of a narrow window; the smallest real step (90 / 110 %) is 77 px at 768.
export const FRAME_PX = 20

const num = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : NaN
}
// Two decimals, so 17/20 prints as 0.85 and compares equal to a literal.
const tidy = (z) => Number(z.toFixed(2))

// 'auto' | 'smaller' | 'larger' (absent, garbage → 'auto').
export function normalizeDisplaySize (value) {
  return value === 'smaller' || value === 'larger' ? value : 'auto'
}

// A path as React Router matches it (App.js routes are not caseSensitive,
// and a trailing slash is ignored): lower case, no trailing slash ('/' for
// the root).  Not a string → ''.
export function routePath (pathname) {
  if (typeof pathname !== 'string') return ''
  const p = pathname.toLowerCase().replace(/\/+$/, '')
  return p || (pathname.startsWith('/') ? '/' : '')
}
const underPrefix = (p, prefix) => p === prefix || p.startsWith(prefix + '/')

// Pop-out windows (/popup/*, the timer bar included) are never scaled.
export function isPopupPath (pathname) {
  return underPrefix(routePath(pathname), '/popup')
}

// D3 ("the sign-in page and any signed-out page use Auto"): the App.js
// routes outside the staff app and the pop-outs, which use Auto even while
// a staff person is signed in on this computer (a staff token in this
// browser must not size a client's page, e.g. signing an engagement letter
// at the front desk): the sign-in page and the account pages, the client
// portal (same origin, its own sign-in), and the pages a client opens from
// a link.  Each covers the path and everything under it.
// lib/displayZoom.test.js reads App.js and fails when a route outside the
// staff app is missing here, or a staff page is in it.
export const AUTO_ONLY_PATHS = Object.freeze([
  '/login', '/register', '/forgot-password', '/reset-password', '/emergency-lock',
  '/portal',
  '/intake', '/sign', '/questionnaire', '/records', '/consult/cancel', '/pay',
])
export function isAutoOnlyPath (pathname) {
  const p = routePath(pathname)
  return AUTO_ONLY_PATHS.some(prefix => underPrefix(p, prefix))
}

// The page zoom the person set (browser Ctrl +/-, desktop View → Zoom), read
// from outerWidth / innerWidth → { bz, known, exact }:
//   - outer within a frame (20 px) or 2 % of inner: no zoom (exact);
//   - else snapped to the nearest known step within 2 %, accepted only when
//     devicePixelRatio / step is a real OS scale;
//   - else unknown (bz 1): a docked side panel, DevTools or Edge's app bar
//     changes the ratio too, and no step explains it.
// A docked panel can also look like a step (OS 125 % + a 20 % panel = Chrome
// at 125 %); resolvePageZoom keeps what was read with nothing docked.
export function readPageZoom ({ outerWidth, innerWidth, devicePixelRatio, isElectron } = {}) {
  const outer = num(outerWidth)
  const inner = num(innerWidth)
  const unknown = { bz: 1, known: false, exact: false }
  if (!(outer > 0) || !(inner > 0)) return unknown
  if (Math.abs(outer - inner) <= Math.max(FRAME_PX, SNAP_TOLERANCE * inner)) return { bz: 1, known: true, exact: true }
  const r = outer / inner
  const steps = isElectron ? DESKTOP_ZOOM_STEPS : BROWSER_ZOOM_STEPS
  let best = 1
  let bestErr = Infinity
  for (const s of steps) {
    const e = Math.abs(r / s - 1)
    if (e < bestErr) { bestErr = e; best = s }
  }
  if (bestErr > SNAP_TOLERANCE || Math.abs(best - 1) < 1e-9) return unknown
  const dpr = num(devicePixelRatio)
  if (!(dpr > 0)) return unknown
  const os = dpr / best
  return OS_SCALES.some(s => Math.abs(os / s - 1) <= OS_TOLERANCE) ? { bz: best, known: true, exact: false } : unknown
}

// The number alone (1 when unknown).
export function detectPageZoom (input) {
  return readPageZoom(input).bz
}

const sameDpr = (a, b) => Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) < 1e-6

// The page zoom to use, with what was read before on this page:
//   remembered = { dpr, bz } from the last readable evaluation.
// A frame-only window is always read fresh (and remembered).  Otherwise, while
// devicePixelRatio is unchanged, the remembered zoom stands — a page-zoom
// change always moves devicePixelRatio, docking a panel never does.  Else
// the reading; an unknown reading is { known: false }.
// → { bz, known, remember } (remember: what to keep for the next evaluation).
export function resolvePageZoom (input = {}, remembered = null) {
  const reading = readPageZoom(input)
  const dpr = num(input.devicePixelRatio) > 0 ? num(input.devicePixelRatio) : 1
  const rem = remembered && Number.isFinite(Number(remembered.bz)) && Number(remembered.bz) > 0
    ? { dpr: Number(remembered.dpr), bz: Number(remembered.bz) }
    : null
  if (reading.exact) return { bz: 1, known: true, remember: { dpr, bz: 1 } }
  if (rem && sameDpr(rem.dpr, dpr)) return { bz: rem.bz, known: true, remember: rem }
  if (reading.known) return { bz: reading.bz, known: true, remember: { dpr, bz: reading.bz } }
  return { bz: 1, known: false, remember: rem }
}

// D2: is this OS scale (devicePixelRatio ÷ page zoom) 150 % or more?
export function isDenseOsScale (osScale) {
  const os = num(osScale)
  return os > 0 && os / DENSE_OS_SCALE >= 1 - OS_TOLERANCE
}

// Auto's zoom for an UNZOOMED window W × H (CSS px at 100 % page zoom; the
// OS's display scaling is already divided out — a 1080p screen at 150 % is a
// 1280-wide window — so screen density needs no DPI) on a screen the OS
// scales by `osScale` (D2's floor).  Unrounded.
//   up (H > 1000): the height curve, at most W / 1536 (D1), never below 1;
//   down: H / 760, floored at 0.85 when osScale ≥ 1.5 (D2).
export function autoZoom (width, height, osScale = 1) {
  const W = num(width)
  const H = num(height)
  if (!(W > 0) || !(H > 0)) return 1
  if (W < MIN_ZOOM_WIDTH) return 1
  const rh = H / UP_REF_HEIGHT
  if (rh > 1) return Math.max(1, Math.min(1 + Math.pow(Math.min(rh, UP_LIMIT) - 1, UP_CURVE), W / AUTO_MIN_ROOM))
  const down = Math.min(1, H / DOWN_REF_HEIGHT)
  return isDenseOsScale(osScale) ? Math.max(DENSE_AUTO_FLOOR, down) : down
}

// The breakpoint whose layout a window `w` CSS px wide gets: the largest of
// BREAKPOINTS (Tailwind's) that is ≤ w, else the 375 px phone.
export function layoutBreakpoint (width) {
  const w = num(width)
  let b = NARROW_DESIGN_WIDTH
  for (const bp of BREAKPOINTS) if (w >= bp) b = bp
  return b
}

// The breakpoint the cap protects: the largest of CAP_BREAKPOINTS (2xl left
// out, D4) that is ≤ w, else the 375 px phone.
export function capBreakpoint (width) {
  const w = num(width)
  let b = NARROW_DESIGN_WIDTH
  for (const bp of CAP_BREAKPOINTS) if (w >= bp) b = bp
  return b
}

// The most a zoom above 1 may be in a window `w` CSS px wide: w / the cap
// breakpoint (≥ 1 always).  1280 → 1.00, 1536 → 1.20, 1920 → 1.50, 2560 → 2.
export function zoomCap (width) {
  const w = num(width)
  if (!(w > 0)) return ZOOM_MAX
  return w / capBreakpoint(w)
}

export function clampZoom (z) {
  const n = num(z)
  if (!Number.isFinite(n)) return 1
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, n))
}

export const roundZoom = (z) => tidy(Math.round(z / ZOOM_STEP) * ZOOM_STEP)
// Down to a 0.05 step (a cap is never rounded up past itself).
export const floorZoom = (z) => tidy(Math.floor(z / ZOOM_STEP + 1e-9) * ZOOM_STEP)

// The unrounded zoom for a window (no hysteresis, no cap), with what went in.
//   input: { innerWidth, innerHeight, outerWidth, devicePixelRatio,
//            isElectron, pathname, displaySize, remembered }
// The person's own page zoom bz COUNTS toward the target instead of
// multiplying with it: the target T is for the unzoomed window; a person who
// zoomed in (bz > 1) keeps at least their zoom (z ≥ 1) and gets more only if
// T is bigger; one who zoomed out keeps at most theirs.  So 4K + Chrome 150 %
// totals 2.0 (z 1.35), not 3.0, and a 720p window at 125 % keeps its 125 %.
// A zoom it cannot read (resolvePageZoom: known false) leaves Auto at 1, so
// an unseen zoom-in is never shrunk back; Display size still applies.
// The screen's OS scale (D2) is devicePixelRatio ÷ bz: Chrome at 80 % on a
// 175 % screen has devicePixelRatio 1.4, and the floor still applies.
// `input.displaySize` is the size in effect (the runtime passes Auto where
// the person's choice does not apply: nobody signed in, isAutoOnlyPath).
export function targetZoom (input = {}) {
  if (isPopupPath(input.pathname)) return { target: 1, pageZoom: 1, pageZoomKnown: true, osScale: 1, auto: 1, popup: true, remember: input.remembered || null }
  const pz = resolvePageZoom(input, input.remembered)
  const bz = pz.bz
  const dpr = num(input.devicePixelRatio) > 0 ? num(input.devicePixelRatio) : 1
  const osScale = dpr / bz
  const auto = pz.known ? autoZoom(num(input.innerWidth) * bz, num(input.innerHeight) * bz, osScale) : 1
  let z = auto
  if (bz > 1) z = Math.max(1, auto / bz)
  else if (bz < 1) z = Math.min(1, auto / bz)
  z *= SIZE_MULTIPLIER[normalizeDisplaySize(input.displaySize)]
  return { target: clampZoom(z), pageZoom: bz, pageZoomKnown: pz.known, osScale, auto, popup: false, remember: pz.remember }
}

// From the unrounded target to the zoom shown, in this order: hysteresis
// (keep `current` while the target is within 0.035 of it — no flicker while
// a window is dragged across a step), then the 0.05 step, then the cap LAST
// (a zoom above 1 never exceeds w / the cap breakpoint: a capped value
// rounds DOWN, and a held value above the cap is dropped at once).
export function settleZoom (target, current, innerWidth) {
  const t = clampZoom(target)
  const held = current != null && Number.isFinite(Number(current)) && Math.abs(t - Number(current)) < HYSTERESIS
  let z = held ? tidy(Number(current)) : roundZoom(t)
  if (z > 1) {
    const cap = zoomCap(innerWidth)
    if (z > cap) z = Math.max(1, floorZoom(cap))
  }
  return tidy(clampZoom(z))
}

// Everything for one evaluation.  `current` = the zoom shown now (null on a
// fresh start or after the Display size changed: no hysteresis).
// `input.remembered`: the page zoom kept from the last evaluation on this page
// (result.remember), or absent on a fresh load.
export function computeDisplayZoom (input = {}, current = null) {
  const t = targetZoom(input)
  if (t.popup) return { zoom: 1, target: 1, pageZoom: 1, pageZoomKnown: true, osScale: 1, auto: 1, cap: ZOOM_MAX, popup: true, remember: t.remember }
  return {
    zoom: settleZoom(t.target, current, input.innerWidth),
    target: t.target,
    pageZoom: t.pageZoom,
    pageZoomKnown: t.pageZoomKnown,
    osScale: t.osScale,
    auto: t.auto,
    cap: zoomCap(input.innerWidth),
    popup: false,
    remember: t.remember,
  }
}

// Chart helpers (Recharts props are px, so they follow the zoom here).
// zoomPx: geometry.  zoomFontPx: text, floored at 9 px — never below today's
// size for text that was already smaller than 9 px, and unchanged at z = 1.
export function zoomPx (px, zoom) {
  const z = num(zoom) > 0 ? num(zoom) : 1
  return Math.round(num(px) * z)
}
export function zoomFontPx (px, zoom) {
  const p = num(px)
  const z = num(zoom) > 0 ? num(zoom) : 1
  return tidy(Math.max(Math.min(p, MIN_TEXT_PX), p * z))
}

// ── The person's choice (per person, on this computer — D3) ─────────────
// localStorage['lextrack_display_size:<user id>'] = 'smaller' | 'larger';
// absent = Auto.  Never sent to the server: a laptop and a 4K desk monitor
// differ, so each person has one per computer.  AuthContext names the
// signed-in user (claimDisplaySizeFor) before every setUser, as it does for
// the quick lists (lib/quickListOwner.js), and clears it on sign-out; with
// nobody named every page is Auto.  Registered in lib/uiPrefs.js "Layout +
// navigation" (the signed-in person's own key only; another person's is
// never listed or cleared), so Reset UI preferences returns to Auto (its
// key-null storage event re-evaluates).  Desktop windows share the
// persist:lextrack partition's localStorage.
const noUser = (uid) => uid === null || uid === undefined || uid === ''

export function displaySizeKeyFor (uid) {
  return `${DISPLAY_SIZE_KEY}:${uid}`
}

// Nobody signed in → 'auto'.
export function readDisplaySize (uid, storage) {
  if (noUser(uid)) return 'auto'
  try {
    const s = storage || (typeof window !== 'undefined' ? window.localStorage : null)
    return normalizeDisplaySize(s ? s.getItem(displaySizeKeyFor(uid)) : null)
  } catch { return 'auto' }
}

// Returns false when there is nobody to keep it for, or storage refused
// (private mode, blocked site data).
export function writeDisplaySize (size, uid, storage) {
  if (noUser(uid)) return false
  const v = normalizeDisplaySize(size)
  try {
    const s = storage || window.localStorage
    if (v === 'auto') s.removeItem(displaySizeKeyFor(uid))
    else s.setItem(displaySizeKeyFor(uid), v)
    return true
  } catch { return false }
}

// The per-computer choice kept before D3 (the bare key) goes to the first
// person who signs in — unless they already have their own — and the bare
// key is removed, so nobody after them inherits it (the quick lists' "no
// recorded owner = the first user keeps them").  The removal is attempted
// even when storing it for them fails (storage full): their choice then
// lasts for this page only (the value returned), and is never left for the
// next person.  Returns the size storage refused to keep, else null.
function migrateUnscopedDisplaySize (uid) {
  let s
  let old
  try {
    s = window.localStorage
    old = s.getItem(DISPLAY_SIZE_KEY)
  } catch { return null } // storage blocked: nothing to hand over
  if (old === null) return null
  const v = normalizeDisplaySize(old)
  let unsaved = null
  if (v !== 'auto') {
    try {
      if (s.getItem(displaySizeKeyFor(uid)) === null) s.setItem(displaySizeKeyFor(uid), v)
    } catch (err) {
      unsaved = v
      console.error('Display size: could not keep the earlier choice for this person', err)
    }
  }
  try { s.removeItem(DISPLAY_SIZE_KEY) } catch (err) { console.error('Display size: could not remove the earlier per-computer choice', err) }
  return unsaved
}

// ── Runtime ──────────────────────────────────────────────────────────────
let state = null // { zoom, target, pageZoom, pageZoomKnown, osScale, auto, cap, popup, size }
let displayUser = null // the signed-in person whose Display size applies (string), or null: Auto
let sessionSize = null // a choice storage refused to keep: this page and this person only
let rememberedPageZoom = null // { dpr, bz }: resolvePageZoom's memory for this page
let printing = false
let started = false
let settleTimer = null
let settleFresh = false
let dprQuery = null
const listeners = new Set()
const windowHandlers = []

function readWindow () {
  const w = window
  let isElectron = false
  try { isElectron = !!(w.electronAPI && w.electronAPI.isElectron) } catch { isElectron = false }
  let pathname = ''
  try { pathname = w.location ? w.location.pathname : '' } catch { pathname = '' }
  return {
    innerWidth: w.innerWidth,
    innerHeight: w.innerHeight,
    outerWidth: w.outerWidth,
    devicePixelRatio: w.devicePixelRatio || 1,
    isElectron,
    pathname,
  }
}

function applyToDom (zoom) {
  try {
    const root = document.documentElement
    root.style.setProperty('--app-zoom', String(zoom))
    root.setAttribute('data-app-zoom', String(zoom))
  } catch { /* no DOM */ }
}

function notify () {
  for (const fn of Array.from(listeners)) {
    try { fn() } catch (err) { console.error('displayZoom listener failed', err) }
  }
}

// The size in effect: the signed-in person's choice, except where nobody is
// signed in and on the signed-out pages (isAutoOnlyPath: the sign-in page,
// the portal, the pages a client opens from a link): Auto.
function sizeInEffect (pathname) {
  if (displayUser === null || isAutoOnlyPath(pathname)) return 'auto'
  return sessionSize || readDisplaySize(displayUser)
}

// Evaluate now and apply.  `fresh`: drop the hysteresis (start, Display size
// change, sign-in / sign-out, another monitor).  Returns the zoom applied.
export function refreshDisplayZoom ({ fresh = false } = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 1
  const win = readWindow()
  const size = sizeInEffect(win.pathname)
  const r = computeDisplayZoom({ ...win, displaySize: size, remembered: rememberedPageZoom }, fresh || !state ? null : state.zoom)
  rememberedPageZoom = r.remember || null
  const shown = { ...r }
  delete shown.remember
  const changed = !state || state.zoom !== r.zoom || state.size !== size ||
    state.target !== r.target || state.pageZoom !== r.pageZoom || state.pageZoomKnown !== r.pageZoomKnown || state.popup !== r.popup
  if (!state || state.zoom !== r.zoom) applyToDom(r.zoom)
  if (changed) {
    state = { ...shown, size }
    notify()
  }
  return r.zoom
}

function settleLater (fresh) {
  if (fresh) settleFresh = true
  clearTimeout(settleTimer)
  settleTimer = setTimeout(() => {
    const f = settleFresh
    settleFresh = false
    refreshDisplayZoom({ fresh: f })
  }, SETTLE_MS)
}

// devicePixelRatio changes when the window moves to a monitor with another
// scale (or the page zoom changes): one-shot resolution query, re-armed.
function watchDevicePixelRatio () {
  try {
    if (dprQuery) dprQuery.mq.removeEventListener('change', dprQuery.fn)
  } catch { /* old engine */ }
  dprQuery = null
  if (typeof window.matchMedia !== 'function') return
  try {
    const mq = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`)
    const fn = () => { watchDevicePixelRatio(); settleLater(true) }
    if (mq && typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', fn)
      dprQuery = { mq, fn }
    }
  } catch { /* no resolution queries */ }
}

// The signed-in person's Display size on this computer (Profile).  Applies
// at once (Auto on isAutoOnlyPath pages, where it is still kept).
// Nobody signed in: nothing to keep it for, nothing changes.
export function setDisplaySize (size) {
  if (displayUser === null) return getDisplayZoom()
  const v = normalizeDisplaySize(size)
  sessionSize = writeDisplaySize(v, displayUser) ? null : v
  return refreshDisplayZoom({ fresh: true })
}

// AuthContext names who is signed in BEFORE every setUser (page load with a
// stored token, every sign-in, and refreshUser, whose /me may answer for
// another person) and passes null on sign-out and when the stored sign-in
// is refused.  A choice from before D3 goes to the first person named.
// Applies at once.
export function claimDisplaySizeFor (uid) {
  const id = noUser(uid) ? null : String(uid)
  const unsaved = id !== null ? migrateUnscopedDisplaySize(id) : null
  if (id !== displayUser) {
    displayUser = id
    sessionSize = null
  }
  if (unsaved) sessionSize = unsaved
  if (started) refreshDisplayZoom({ fresh: true })
}

// Whose Display size applies now (lib/uiPrefs.js lists only theirs).
export function getDisplaySizeUser () {
  return displayUser
}

// The zoom JS sizes should use (charts, px popups): 1 while printing.
export function getDisplayZoom () {
  if (printing) return 1
  return state ? state.zoom : 1
}

// { zoom, target, pageZoom, pageZoomKnown, osScale, auto, cap, popup, size }
// — a stable object between changes (useSyncExternalStore).  size = the
// size in effect on this page.
const IDLE_STATE = Object.freeze({ zoom: 1, target: 1, pageZoom: 1, pageZoomKnown: true, osScale: 1, auto: 1, cap: ZOOM_MAX, popup: false, size: 'auto' })
export function getDisplayZoomState () {
  return state || IDLE_STATE
}

export function subscribeDisplayZoom (fn) {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

// Called once by index.js before the first render (no reflow flash; nobody
// is signed in yet, so Auto until AuthContext's claimDisplaySizeFor, which
// runs before anything signed-in renders).  Re-evaluates 200 ms after the
// last resize, on a devicePixelRatio change, on a storage event for the
// signed-in person's key (they chose a size in another window of this
// computer) or the bare key (another window handed it over), or the
// key-null event of Reset UI preferences / a bulk clear.  Another person's
// key is ignored.
export function startDisplayZoom () {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 1
  if (started) return getDisplayZoom()
  started = true
  const z = refreshDisplayZoom({ fresh: true })
  const on = (type, fn) => { window.addEventListener(type, fn); windowHandlers.push([type, fn]) }
  on('resize', () => settleLater(false))
  on('storage', (e) => {
    if (!e || e.key === null || e.key === undefined) { sessionSize = null; settleLater(true); return }
    if (e.key === DISPLAY_SIZE_KEY || (displayUser !== null && e.key === displaySizeKeyFor(displayUser))) { sessionSize = null; settleLater(true) }
  })
  on('beforeprint', () => { if (!printing) { printing = true; notify() } })
  on('afterprint', () => { if (printing) { printing = false; notify() } })
  watchDevicePixelRatio()
  return z
}

// Tests only: forget the runtime state.
export function __resetDisplayZoomForTests () {
  clearTimeout(settleTimer)
  for (const [type, fn] of windowHandlers.splice(0)) {
    try { window.removeEventListener(type, fn) } catch { /* no window */ }
  }
  try { if (dprQuery) dprQuery.mq.removeEventListener('change', dprQuery.fn) } catch { /* old engine */ }
  state = null
  displayUser = null
  sessionSize = null
  rememberedPageZoom = null
  printing = false
  started = false
  settleTimer = null
  settleFresh = false
  dprQuery = null
  listeners.clear()
}
